// Class progression panel — a clean level-by-level table of what a class grants:
// proficiency bonus, features (with descriptions on hover/expand), ASI levels, and
// spell slots. Powered by /api/srd/class/{class}/progression. Highlights the
// character's current level so a player sees what's earned and what's ahead.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { ClassProgression } from "../lib/types";
import { titleizeSlug } from "../lib/format";

export function ClassProgressionPanel({ classIndex, currentLevel }: { classIndex: string; currentLevel?: number }) {
  const [prog, setProg] = useState<ClassProgression | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    setProg(null);
    api.classProgression(classIndex).then((p) => alive && setProg(p)).catch(() => {});
    return () => { alive = false; };
  }, [classIndex]);

  if (!prog) return <div className="muted" style={{ padding: 8 }}>Loading progression…</div>;

  // Does this class get spell slots anywhere?
  const hasSlots = prog.levels.some((l) => Object.keys(l.spell_slots).length > 0);
  const maxSlotLevel = Math.max(
    1, ...prog.levels.flatMap((l) => Object.keys(l.spell_slots).map(Number)),
  );

  return (
    <div className="prog">
      <div className="prog__head">
        <span>{titleizeSlug(prog.name)} — d{prog.hit_die} Hit Die</span>
        <span className="muted">Tap a level for feature details</span>
      </div>
      <div className="prog__table">
        <div className="prog__row prog__row--header">
          <span className="prog__lvl">Lvl</span>
          <span className="prog__pb">Prof</span>
          <span className="prog__feats">Features</span>
          {hasSlots && <span className="prog__slots">Spell Slots</span>}
        </div>
        {prog.levels.map((l) => {
          const isCurrent = currentLevel === l.level;
          const featNames = l.features.map((f) => f.name);
          if (l.asi) featNames.push("Ability Score Improvement / Feat");
          const slotStr = Object.keys(l.spell_slots).length
            ? Array.from({ length: maxSlotLevel }, (_, i) => l.spell_slots[String(i + 1)] || 0)
                .map((n) => (n ? n : "·")).join(" ")
            : (l.cantrips_known ? "" : "—");
          return (
            <div key={l.level}>
              <div
                className={`prog__row${isCurrent ? " prog__row--current" : ""}${l.features.length ? " prog__row--click" : ""}`}
                onClick={() => l.features.length && setOpen(open === l.level ? null : l.level)}
              >
                <span className="prog__lvl">{l.level}</span>
                <span className="prog__pb">+{l.prof_bonus}</span>
                <span className="prog__feats">
                  {featNames.length ? featNames.join(", ") : <span className="muted">—</span>}
                </span>
                {hasSlots && <span className="prog__slots">{slotStr}</span>}
              </div>
              {open === l.level && l.features.length > 0 && (
                <div className="prog__detail">
                  {l.features.map((f) => (
                    <div key={f.index} className="prog__feat">
                      <div className="prog__feat-name">{f.name}</div>
                      {f.desc && <div className="prog__feat-desc">{f.desc.split("\n\n")[0]}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {hasSlots && (
        <div className="prog__legend">Spell slot columns are levels 1–{maxSlotLevel} (· = none).</div>
      )}
    </div>
  );
}
